/**
 * HTTP-клиент с поддержкой cookie-авторизации и CSRF.
 *
 * - credentials: 'include' — отправляет httpOnly cookies автоматически
 * - CSRF: читает XSRF-TOKEN cookie и отправляет X-XSRF-TOKEN header
 * - Auto-refresh: при 401 пробует POST /refresh, затем retry оригинальный запрос
 */

/** Ошибка API с типизированной структурой */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown = null,
  ) {
    super(`HTTP ${status}: ${statusText}`)
    this.name = 'ApiError'
  }
}

/** Чтение значения cookie по имени */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

/** Флаг: refresh запрос уже выполняется (предотвращает параллельные refresh) */
let refreshInProgress: Promise<boolean> | null = null

/** Попытка обновить токены через refresh cookie */
async function tryRefresh(): Promise<boolean> {
  // Дедупликация: если refresh уже идет, ждем его результат
  if (refreshInProgress) {
    return refreshInProgress
  }

  refreshInProgress = (async () => {
    try {
      const csrfToken = getCookie('XSRF-TOKEN')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (csrfToken) {
        headers['X-XSRF-TOKEN'] = csrfToken
      }

      const response = await fetch('/api/v1/account/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers,
      })

      return response.ok
    } catch {
      return false
    }
  })()

  try {
    return await refreshInProgress
  } finally {
    refreshInProgress = null
  }
}

/** Общий метод запроса */
async function request<T>(
  url: string,
  options: RequestInit = {},
  retry = true,
): Promise<T> {
  const headers = new Headers(options.headers)

  // CSRF: для мутирующих методов добавляем X-XSRF-TOKEN
  const method = (options.method ?? 'GET').toUpperCase()
  if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
    const csrfToken = getCookie('XSRF-TOKEN')
    if (csrfToken) {
      headers.set('X-XSRF-TOKEN', csrfToken)
    }
  }

  // Content-Type по умолчанию для JSON body
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  })

  // 401 — попытка refresh + retry
  if (response.status === 401 && retry) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      return request<T>(url, options, false)
    }

    throw new ApiError(401, 'Unauthorized')
  }

  // Обработка ошибок
  if (!response.ok) {
    let body: unknown = null
    try {
      body = await response.json()
    } catch {
      // Тело ответа не JSON
    }
    throw new ApiError(response.status, response.statusText, body)
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

/** Типизированные методы API-клиента */
export const api = {
  get<T>(url: string): Promise<T> {
    return request<T>(url)
  },

  post<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'POST',
      body: body != null ? JSON.stringify(body) : undefined,
    })
  },

  put<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'PUT',
      body: body != null ? JSON.stringify(body) : undefined,
    })
  },

  patch<T>(url: string, body?: unknown): Promise<T> {
    return request<T>(url, {
      method: 'PATCH',
      body: body != null ? JSON.stringify(body) : undefined,
    })
  },

  delete<T>(url: string): Promise<T> {
    return request<T>(url, { method: 'DELETE' })
  },
}
